import React, { useEffect, useMemo, useRef } from "react";
import {
  Box,
  Button,
  Chip,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from "@mui/material";

const typeLabelMap = {
  objection: "Заперечення",
  faq: "FAQ",
  note: "Нотатка",
  script: "Скрипт",
};

const typeStyleMap = {
  objection: {
    borderColor: "rgba(239, 68, 68, 0.35)",
    backgroundColor: "rgba(239, 68, 68, 0.06)",
    chipColor: "error",
  },
  faq: {
    borderColor: "rgba(59, 130, 246, 0.35)",
    backgroundColor: "rgba(59, 130, 246, 0.06)",
    chipColor: "info",
  },
  note: {
    borderColor: "rgba(107, 114, 128, 0.35)",
    backgroundColor: "rgba(107, 114, 128, 0.05)",
    chipColor: "default",
  },
  script: {
    borderColor: "rgba(99, 102, 241, 0.35)",
    backgroundColor: "rgba(99, 102, 241, 0.06)",
    chipColor: "secondary",
  },
};

const ItemEditor = ({
  item,
  onChange,
  onDraftChange,
  onDelete,
  onMoveUp,
  onMoveDown,
  disableMoveUp = false,
  disableMoveDown = false,
}) => {
  const tagsValue = (item.tags || []).join(", ");
  const bodyRef = useRef(null);
  const draftQueueRef = useRef({});
  const rafRef = useRef(0);
  const typeStyles = useMemo(
    () => typeStyleMap[item.item_type] || {},
    [item.item_type]
  );

  const pushDraft = (updates) => {
    if (onDraftChange) {
      onDraftChange(updates);
      return;
    }
    onChange(updates);
  };

  const commitDraft = (updates) => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = 0;
    }
    if (Object.keys(draftQueueRef.current).length) {
      const pending = draftQueueRef.current;
      draftQueueRef.current = {};
      pushDraft(pending);
    }
    if (onDraftChange) {
      onChange(updates);
      return;
    }
    onChange(updates);
  };

  const flushDraft = () => {
    rafRef.current = 0;
    const pending = draftQueueRef.current;
    draftQueueRef.current = {};
    if (Object.keys(pending).length) {
      pushDraft(pending);
    }
  };

  const queueDraft = (updates) => {
    draftQueueRef.current = { ...draftQueueRef.current, ...updates };
    if (!rafRef.current) {
      rafRef.current = requestAnimationFrame(flushDraft);
    }
  };

  useEffect(() => {
    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, []);

  const updateBodyValue = (nextValue) => {
    if (bodyRef.current) {
      bodyRef.current.value = nextValue;
    }
    queueDraft({ body: nextValue });
  };

  const applyWrap = (before, after = before) => {
    const textarea = bodyRef.current;
    if (!textarea) return;
    const value = textarea.value || "";
    const start = textarea.selectionStart ?? value.length;
    const end = textarea.selectionEnd ?? value.length;
    let nextValue = value;
    let nextStart = start;
    let nextEnd = end;

    if (start !== end) {
      nextValue =
        value.slice(0, start) +
        before +
        value.slice(start, end) +
        after +
        value.slice(end);
      nextStart = start + before.length;
      nextEnd = end + before.length;
    } else {
      nextValue = value.slice(0, start) + before + after + value.slice(end);
      nextStart = start + before.length;
      nextEnd = start + before.length;
    }

    updateBodyValue(nextValue);
    requestAnimationFrame(() => {
      if (!bodyRef.current) return;
      bodyRef.current.focus();
      bodyRef.current.setSelectionRange(nextStart, nextEnd);
    });
  };

  const applyLinePrefix = (prefix) => {
    const textarea = bodyRef.current;
    if (!textarea) return;
    const value = textarea.value || "";
    const start = textarea.selectionStart ?? value.length;
    const end = textarea.selectionEnd ?? value.length;
    const hasSelection = start !== end;
    const selected = value.slice(start, end);
    const lines = (hasSelection ? selected : "").split("\n");
    const nextLines = hasSelection
      ? lines.map((line) => (line ? `${prefix}${line}` : line))
      : [prefix];
    const nextValue =
      value.slice(0, start) +
      nextLines.join("\n") +
      value.slice(hasSelection ? end : start);
    const cursor = start + nextLines.join("\n").length;
    updateBodyValue(nextValue);
    requestAnimationFrame(() => {
      if (!bodyRef.current) return;
      bodyRef.current.focus();
      bodyRef.current.setSelectionRange(cursor, cursor);
    });
  };

  const selectionRef = useRef({ start: 0, end: 0 });

  const updateSelection = () => {
    const textarea = bodyRef.current;
    if (!textarea) return;
    selectionRef.current = {
      start: textarea.selectionStart ?? 0,
      end: textarea.selectionEnd ?? 0,
    };
  };

  const applyLink = () => {
    const textarea = bodyRef.current;
    if (!textarea) return;
    const value = textarea.value || "";
    const start = textarea.selectionStart ?? value.length;
    const end = textarea.selectionEnd ?? value.length;
    const selected = value.slice(start, end) || "текст";
    const insert = `[${selected}](https://)`;
    const nextValue = value.slice(0, start) + insert + value.slice(end);
    const cursor = start + insert.length - 1;
    updateBodyValue(nextValue);
    requestAnimationFrame(() => {
      if (!bodyRef.current) return;
      bodyRef.current.focus();
      bodyRef.current.setSelectionRange(cursor, cursor);
    });
  };

  const applyColor = (event) => {
    const value = event.target.value;
    if (!value) return;
    const { start, end } = selectionRef.current;
    if (start === end) return;
    const current = bodyRef.current?.value || "";
    const nextValue =
      current.slice(0, start) +
      `[color=${value}]` +
      current.slice(start, end) +
      "[/color]" +
      current.slice(end);
    updateBodyValue(nextValue);
    requestAnimationFrame(() => {
      if (!bodyRef.current) return;
      bodyRef.current.focus();
      const cursor =
        end + `[color=${value}]`.length + "[/color]".length;
      bodyRef.current.setSelectionRange(cursor, cursor);
    });
    selectionRef.current = { start: 0, end: 0 };
  };

  const applyFontSize = (event) => {
    const value = event.target.value;
    if (!value) return;
    const { start, end } = selectionRef.current;
    if (start === end) return;
    const current = bodyRef.current?.value || "";
    const nextValue =
      current.slice(0, start) +
      `[size=${value}]` +
      current.slice(start, end) +
      "[/size]" +
      current.slice(end);
    updateBodyValue(nextValue);
    requestAnimationFrame(() => {
      if (!bodyRef.current) return;
      bodyRef.current.focus();
      const cursor = end + `[size=${value}]`.length + "[/size]".length;
      bodyRef.current.setSelectionRange(cursor, cursor);
    });
    selectionRef.current = { start: 0, end: 0 };
  };

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        borderColor: typeStyles.borderColor,
        backgroundColor: typeStyles.backgroundColor,
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
        <Typography variant="subtitle2" fontWeight={600}>
          Елемент
        </Typography>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          {item.item_type && (
            <Chip
              size="small"
              label={typeLabelMap[item.item_type] || item.item_type}
              color={typeStyles.chipColor || "default"}
              variant="outlined"
              sx={{ alignSelf: "center" }}
            />
          )}
          <Button
            size="small"
            variant="outlined"
            onClick={onMoveUp}
            disabled={disableMoveUp}
          >
            Вгору
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={onMoveDown}
            disabled={disableMoveDown}
          >
            Вниз
          </Button>
          <Button size="small" color="error" onClick={onDelete}>
            Видалити
          </Button>
        </Box>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 2fr" },
          gap: 2,
          mt: 2,
        }}
      >
        <TextField
          label="Тип"
          select
          value={item.item_type || ""}
          onChange={(event) => onChange({ item_type: event.target.value })}
          fullWidth
        >
          <MenuItem value="">(не вибрано)</MenuItem>
          <MenuItem value="objection">Заперечення</MenuItem>
          <MenuItem value="faq">FAQ</MenuItem>
          <MenuItem value="note">Примітка</MenuItem>
          <MenuItem value="script">Скрипт</MenuItem>
        </TextField>
        <TextField
          label="Заголовок"
          defaultValue={item.title || ""}
          onChange={(event) => queueDraft({ title: event.target.value })}
          onBlur={(event) => commitDraft({ title: event.target.value })}
          fullWidth
        />
      </Box>

      <TextField
        label="Текст"
        defaultValue={item.body || ""}
        onChange={(event) => queueDraft({ body: event.target.value })}
        onBlur={(event) => commitDraft({ body: event.target.value })}
        multiline
        rows={4}
        fullWidth
        sx={{ mt: 2 }}
        inputRef={bodyRef}
        onSelect={updateSelection}
      />
      <Box sx={{ display: "flex", gap: 1, mt: 1, flexWrap: "wrap" }}>
        <Button size="small" variant="outlined" onClick={() => applyWrap("**")}>
          Жирний
        </Button>
        <Button size="small" variant="outlined" onClick={() => applyWrap("*")}>
          Курсив
        </Button>
        <Button size="small" variant="outlined" onClick={() => applyWrap("__")}>
          Підкреслення
        </Button>
        <Button size="small" variant="outlined" onClick={() => applyWrap("~~")}>
          Закреслення
        </Button>
        <Button size="small" variant="outlined" onClick={applyLink}>
          Посилання
        </Button>
        <Button
          size="small"
          variant="outlined"
          onClick={() => applyLinePrefix("- ")}
        >
          Список
        </Button>
        <Button
          size="small"
          variant="outlined"
          onClick={() => applyLinePrefix("1. ")}
        >
          Нумерований
        </Button>
        <Button
          size="small"
          variant="outlined"
          component="label"
          sx={{ gap: 1 }}
        >
          Колір
          <span
            style={{
              width: 12,
              height: 12,
              borderRadius: "50%",
              background: "#3b82f6",
              display: "inline-block",
            }}
          />
          <input
            type="color"
            onChange={applyColor}
            style={{ position: "absolute", opacity: 0, pointerEvents: "none" }}
          />
        </Button>
        <TextField
          select
          size="small"
          label="Розмір"
          defaultValue="16"
          onChange={applyFontSize}
          sx={{ minWidth: 110 }}
          SelectProps={{
            MenuProps: {
              disablePortal: true,
              anchorOrigin: { vertical: "bottom", horizontal: "left" },
              transformOrigin: { vertical: "top", horizontal: "left" },
              sx: { "& .MuiPaper-root": { mt: 1 } },
            },
          }}
        >
          <MenuItem value="12">12 px</MenuItem>
          <MenuItem value="14">14 px</MenuItem>
          <MenuItem value="16">16 px</MenuItem>
          <MenuItem value="18">18 px</MenuItem>
          <MenuItem value="20">20 px</MenuItem>
          <MenuItem value="24">24 px</MenuItem>
          <MenuItem value="28">28 px</MenuItem>
        </TextField>
      </Box>

      <TextField
        label="Теги (через кому)"
        defaultValue={tagsValue}
        onChange={(event) =>
          queueDraft({
            tags: event.target.value
              .split(",")
              .map((tag) => tag.trim())
              .filter(Boolean),
          })
        }
        onBlur={(event) =>
          commitDraft({
            tags: event.target.value
              .split(",")
              .map((tag) => tag.trim())
              .filter(Boolean),
          })
        }
        fullWidth
        sx={{ mt: 2 }}
      />
    </Paper>
  );
};

export default ItemEditor;
